<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { EventBus, sendMessage } from "@/websocket";
import { cardReaderStatus } from "@/utils/cardReaderStatus";
import RfidDevicePanel from "@/components/RfidDevicePanel.vue";

export default {
  page() {
    return {
      title: this.title,
      meta: [{ name: "description" }],
    };
  },
  components: { Layout, PageHeader, RfidDevicePanel },
  data() {
    return {
      title: "",
      items: [],
      loading: false,
      messagelist: [],
      userinfo: [],
      cardport: "",
      listTimer: null,
    };
  },
  computed: {
    readerStatus() {
      return cardReaderStatus;
    },
    serviceOnline() {
      return ["ready", "connected"].includes(this.readerStatus.state);
    },
    connectedPort() {
      return this.readerStatus.port || this.cardport || "";
    },
    statusText() {
      return this.$t(`cardreader.status.${this.readerStatus.state}`);
    },
    statusHint() {
      return this.$t(`cardreader.status.tooltip.${this.readerStatus.state}`);
    },
    currentDeviceText() {
      if (this.readerStatus.state === "connected" && (this.readerStatus.deviceName || this.connectedPort)) {
        return this.readerStatus.deviceName || this.connectedPort;
      }
      return this.$t("menuitems.clouds.nfcreader.noDevice");
    },
    emptyDescription() {
      if (this.readerStatus.state === "connecting") {
        return this.$t("menuitems.clouds.nfcreader.emptyConnecting");
      }
      if (!this.serviceOnline) {
        return this.$t("menuitems.clouds.nfcreader.emptyOffline");
      }
      return this.$t("menuitems.clouds.nfcreader.emptyReady");
    },
    statusTagType() {
      if (this.readerStatus.state === "connected") return "success";
      if (this.readerStatus.state === "error") return "danger";
      if (this.readerStatus.state === "connecting") return "warning";
      if (this.readerStatus.state === "ready") return "";
      return "info";
    },
    driverDownloadUrl() {
      return this.isAppleSiliconMac()
        ? "https://update.aicoiot.com/driver/AICOIOT_JSSDK_Mac.php"
        : "https://update.aicoiot.com/driver/AICOIOT_JSSDK.php";
    },
  },
  watch: {
    serviceOnline(online) {
      if (online) {
        this.fetchDeviceList(false);
      } else {
        this.messagelist = [];
        this.loading = false;
      }
    },
    "readerStatus.port"(port) {
      this.cardport = port || localStorage.getItem("cardreader") || "";
    },
  },
  created() {
    EventBus.$on("ws-message", this.handleMessage);
  },
  beforeDestroy() {
    EventBus.$off("ws-message", this.handleMessage);
    this.clearListTimer();
  },
  mounted() {
    this.title = this.$t("menuitems.clouds.nfcreader.text");
    this.items = [
      {
        text: this.$t("menuitems.clouds.text"),
        href: "/",
      },
      {
        text: this.$t("menuitems.clouds.nfcreader.text"),
        active: true,
      },
    ];
    this.userinfo = JSON.parse(localStorage.getItem("user") || "null") || [];
    this.cardport = this.readerStatus.port || localStorage.getItem("cardreader") || "";
    if (this.serviceOnline) {
      this.fetchDeviceList(false);
    }
  },
  methods: {
    clearListTimer() {
      if (this.listTimer) {
        clearTimeout(this.listTimer);
        this.listTimer = null;
      }
    },
    ensureService() {
      if (this.serviceOnline) {
        return true;
      }
      this.$message.warning(this.$t("menuitems.clouds.nfcreader.serviceOffline"));
      return false;
    },
    sendCommand(command) {
      if (!this.ensureService()) {
        return false;
      }
      const sent = sendMessage(JSON.stringify(command));
      if (!sent) {
        this.$message.warning(this.$t("menuitems.clouds.nfcreader.serviceOffline"));
      }
      return sent;
    },
    handleMessage(msg) {
      let data;
      try {
        data = typeof msg === "string" ? JSON.parse(msg) : msg;
      } catch (e) {
        return;
      }
      if (!data || !Array.isArray(data.devices)) {
        return;
      }
      this.messagelist = data.devices;
      this.loading = false;
      this.clearListTimer();
      this.cardport = this.readerStatus.port || localStorage.getItem("cardreader") || "";
    },
    fetchDeviceList(showToast = true) {
      if (!this.sendCommand({ type: "command", command: "getlist" })) {
        return;
      }
      this.loading = true;
      this.clearListTimer();
      this.listTimer = setTimeout(() => {
        this.loading = false;
      }, 4000);
      if (showToast) {
        this.$message.success(this.$t("menuitems.clouds.nfcreader.refreshSent"));
      }
    },
    updatev2(port) {
      this.$confirm(
        this.$t("menuitems.clouds.nfcreader.tips.update"),
        this.$t("menuitems.clouds.nfcreader.tips.warning"),
        {
          confirmButtonText: this.$t("global.button.ok"),
          cancelButtonText: this.$t("global.button.cancel"),
          type: "warning",
        }
      ).then(() => {
        this.sendCommand({
          type: "command",
          command: "update",
          device: port,
        });
      });
    },
    disconnect(port) {
      this.$confirm(
        this.$t("menuitems.clouds.nfcreader.tips.disconnect"),
        this.$t("menuitems.clouds.nfcreader.tips.warning"),
        {
          confirmButtonText: this.$t("global.button.ok"),
          cancelButtonText: this.$t("global.button.cancel"),
          type: "warning",
        }
      ).then(() => {
        this.sendCommand({
          type: "command",
          command: "disconnect",
          device: port,
        });
      });
    },
    autoconnect() {
      if (!this.sendCommand({ type: "command", command: "scan", baud: 9600 })) {
        return;
      }
      this.$message.success(this.$t("menuitems.clouds.nfcreader.scanSent"));
    },
    isCurrentDevice(port) {
      return !!this.connectedPort && this.connectedPort == port;
    },
    tableRowClass({ row }) {
      return this.isCurrentDevice(row.port) ? "nfc-current-row" : "";
    },
    isMacOS() {
      if (typeof navigator === "undefined") return false;
      const platform = navigator.platform || "";
      const ua = navigator.userAgent || "";
      if (/iPhone|iPad|iPod/i.test(ua)) return false;
      return /Mac|Macintosh/i.test(platform) || /Macintosh|Mac OS X/i.test(ua);
    },
    isAppleSiliconMac() {
      if (!this.isMacOS()) return false;
      const ua = navigator.userAgent || "";
      if (/arm64|aarch64|Apple Silicon/i.test(ua)) return true;
      try {
        const canvas = document.createElement("canvas");
        const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
        if (gl) {
          const ext = gl.getExtension("WEBGL_debug_renderer_info");
          if (ext) {
            const renderer = String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) || "");
            if (/Apple M\d/i.test(renderer)) return true;
            if (/Intel|AMD|NVIDIA|Radeon|GeForce/i.test(renderer)) return false;
          }
        }
      } catch (e) {
        // ignore and fall through
      }
      return true;
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card nfc-status-card" :class="'is-' + readerStatus.state">
          <div class="card-body">
            <div class="nfc-status-main">
              <span class="nfc-status-dot"></span>
              <div class="nfc-status-copy">
                <div class="nfc-status-title">
                  {{ $t("menuitems.clouds.nfcreader.text") }}
                  <el-tag size="small" :type="statusTagType" effect="plain">
                    {{ statusText }}
                  </el-tag>
                </div>
                <p class="nfc-status-hint mb-1">{{ statusHint }}</p>
                <p class="nfc-status-device mb-0">
                  {{ $t("menuitems.clouds.nfcreader.currentDevice") }}：
                  <strong>{{ currentDeviceText }}</strong>
                  <span v-if="connectedPort" class="text-muted ml-1">({{ connectedPort }})</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body" v-loading="loading">
            <div class="nfc-toolbar">
              <el-button type="primary" :disabled="!serviceOnline" @click="fetchDeviceList(true)">
                <i class="el-icon-refresh"></i>
                {{ $t("menuitems.clouds.nfcreader.refresh") }}
              </el-button>
              <el-button type="primary" :disabled="!serviceOnline" @click="autoconnect">
                <i class="el-icon-search"></i>
                {{ $t("menuitems.clouds.nfcreader.autoconnect") }}
              </el-button>
              <a
                class="el-button el-button--default"
                :href="driverDownloadUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i class="el-icon-download"></i>
                {{ $t("menuitems.clouds.nfcreader.downloadDriver") }}
              </a>
              <span class="nfc-toolbar-hint text-muted">{{ $t("menuitems.clouds.nfcreader.helperHint") }}</span>
            </div>
            <el-table
              v-if="messagelist.length"
              :data="messagelist"
              stripe
              style="width: 100%"
              :row-class-name="tableRowClass"
            >
              <el-table-column prop="port" :label="$t('menuitems.clouds.nfcreader.port')" min-width="140">
              </el-table-column>
              <el-table-column :label="$t('menuitems.clouds.nfcreader.name')" min-width="200">
                <template slot-scope="scope">
                  <span>{{ scope.row.description || "-" }}</span>
                </template>
              </el-table-column>
              <el-table-column :label="$t('menuitems.clouds.nfcreader.status')" min-width="140">
                <template slot-scope="scope">
                  <el-tag v-if="isCurrentDevice(scope.row.port)" type="success" size="small">
                    {{ $t("menuitems.clouds.nfcreader.connectedTag") }}
                  </el-tag>
                  <el-tag v-else type="info" size="small" effect="plain">
                    {{ $t("menuitems.clouds.nfcreader.available") }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column :label="$t('menuitems.clouds.nfcreader.action')" min-width="220">
                <template slot-scope="scope">
                  <el-button type="primary" size="small" :disabled="!serviceOnline" @click="updatev2(scope.row.port)">
                    {{ $t("menuitems.clouds.nfcreader.command.update") }}
                  </el-button>
                  <el-button
                    v-if="isCurrentDevice(scope.row.port)"
                    type="warning"
                    size="small"
                    :disabled="!serviceOnline"
                    @click="disconnect(scope.row.port)"
                  >
                    {{ $t("menuitems.clouds.nfcreader.command.disconnect") }}
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
            <el-empty v-else :description="emptyDescription"></el-empty>
          </div>
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <RfidDevicePanel :online="serviceOnline" :connected="readerStatus.state === 'connected'" />
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" scoped>
.nfc-status-card {
  border-left: 4px solid #adb5bd;

  &.is-connected {
    border-left-color: #34c38f;
  }

  &.is-ready {
    border-left-color: #556ee6;
  }

  &.is-connecting {
    border-left-color: #f1b44c;
  }

  &.is-error {
    border-left-color: #f46a6a;
  }
}

.nfc-status-main {
  display: flex;
  align-items: flex-start;
}

.nfc-status-dot {
  width: 10px;
  height: 10px;
  margin-top: 7px;
  margin-right: 12px;
  border-radius: 50%;
  background: #adb5bd;
  flex-shrink: 0;
}

.nfc-status-card.is-connected .nfc-status-dot {
  background: #34c38f;
  box-shadow: 0 0 0 4px rgba(52, 195, 143, 0.16);
}

.nfc-status-card.is-ready .nfc-status-dot {
  background: #556ee6;
}

.nfc-status-card.is-connecting .nfc-status-dot {
  background: #f1b44c;
  animation: nfc-pulse 1.2s ease-in-out infinite;
}

.nfc-status-card.is-error .nfc-status-dot {
  background: #f46a6a;
}

.nfc-status-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 6px;
}

.nfc-status-hint,
.nfc-status-device {
  color: #74788d;
  font-size: 13px;
}

.nfc-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.nfc-toolbar-hint {
  font-size: 13px;
}

@keyframes nfc-pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.45;
    transform: scale(0.75);
  }
}
</style>

<style>
.nfc-current-row {
  background: #f3faf6 !important;
}
</style>
